import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/lib/auth";
import { Newsletter, YouTubeApi, Events, Booking, Settings } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, RefreshCw } from "lucide-react";
import { api as axiosApi } from "@/lib/api";

const TABS = [
    { id: "overview", label: "Overview" },
    { id: "newsletter", label: "Newsletter" },
    { id: "subscribers", label: "Subscribers" },
    { id: "youtube", label: "YouTube" },
    { id: "events", label: "Events" },
    { id: "booking", label: "Sessions" },
    { id: "settings", label: "Settings" },
];

export default function Admin() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("overview");

    useEffect(() => {
        if (!loading && !user) navigate("/login", { replace: true });
        else if (!loading && user && !user.is_admin) {
            toast.error("Admin access required.");
            navigate("/", { replace: true });
        }
    }, [loading, user, navigate]);

    if (loading || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="overline">Loading…</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="admin-page">
            <Header />
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-10 pb-16">
                <div className="panel p-8 md:p-10 flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="overline-cobalt mb-3">§ Operator Console</div>
                        <h1
                            className="font-display"
                            style={{ fontSize: "clamp(40px, 5vw, 72px)", lineHeight: 0.98 }}
                        >
                            Admin.
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="font-mono-tech text-[11px]" style={{ color: "var(--muted)" }}>
                            {user.email}
                        </div>
                        <button
                            onClick={async () => { await logout(); navigate("/"); }}
                            className="btn-ghost btn-small"
                            data-testid="admin-logout"
                        >
                            <LogOut size={12} /> Logout
                        </button>
                    </div>
                </div>

                <div
                    className="mt-4 flex flex-wrap panel"
                    data-testid="admin-tabs"
                >
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className="px-5 py-4 font-mono-tech text-[10.5px] tracking-[0.22em] uppercase transition-colors border-r last:border-r-0"
                            style={{
                                color: tab === t.id ? "var(--cobalt)" : "var(--ink-2)",
                                background: tab === t.id ? "var(--panel-2)" : "transparent",
                                borderColor: "var(--rule-soft)",
                                borderBottom: tab === t.id ? "2px solid var(--cobalt)" : "2px solid transparent",
                            }}
                            data-testid={`tab-${t.id}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="mt-5">
                    {tab === "overview" && <OverviewTab />}
                    {tab === "newsletter" && <NewsletterTab />}
                    {tab === "subscribers" && <SubscribersTab />}
                    {tab === "youtube" && <YouTubeTab />}
                    {tab === "events" && <EventsTab />}
                    {tab === "booking" && <BookingTab />}
                    {tab === "settings" && <SettingsTab />}
                </div>
            </div>
            <Footer />
        </div>
    );
}

function StatCard({ k, v, italic = false }) {
    return (
        <div className="panel p-8">
            <div className="overline mb-3">{k}</div>
            <div
                style={{
                    fontFamily: italic ? "'Fraunces', serif" : "'Inter', sans-serif",
                    fontStyle: italic ? "italic" : "normal",
                    fontWeight: italic ? 400 : 900,
                    fontSize: 56,
                    lineHeight: 0.95,
                    letterSpacing: "-0.025em",
                    color: "var(--cobalt)",
                }}
            >
                {v}
            </div>
        </div>
    );
}

function OverviewTab() {
    const [stats, setStats] = useState({ subscribers: 0, events: 0, videos: 0, posts: 0, bookings: 0, slots: 0 });
    useEffect(() => {
        (async () => {
            const [subs, events, videos, posts, bookings, slots] = await Promise.all([
                Newsletter.subscribers().catch(() => []),
                Events.list().catch(() => []),
                YouTubeApi.list().catch(() => []),
                Newsletter.posts().catch(() => []),
                Booking.bookings().catch(() => []),
                Booking.allSlots().catch(() => []),
            ]);
            setStats({
                subscribers: subs.length, events: events.length, videos: videos.length,
                posts: posts.length, bookings: bookings.length, slots: slots.length,
            });
        })();
    }, []);
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5" data-testid="overview-stats">
            <StatCard k="Subscribers" v={stats.subscribers} />
            <StatCard k="Dispatches" v={stats.posts} />
            <StatCard k="Videos" v={stats.videos} />
            <StatCard k="Events" v={stats.events} />
            <StatCard k="Bookings" v={stats.bookings} italic />
            <StatCard k="Total Slots" v={stats.slots} />
        </div>
    );
}

function NewsletterTab() {
    const [posts, setPosts] = useState([]);
    const [subs, setSubs] = useState([]);
    const [form, setForm] = useState({ title: "", excerpt: "", url: "", issue_number: "" });
    const refresh = async () => {
        setPosts(await Newsletter.posts().catch(() => []));
        setSubs(await Newsletter.subscribers().catch(() => []));
    };
    useEffect(() => { refresh(); }, []);
    const submit = async (e) => {
        e.preventDefault();
        try {
            await Newsletter.createPost({
                ...form,
                issue_number: form.issue_number ? Number(form.issue_number) : null,
            });
            setForm({ title: "", excerpt: "", url: "", issue_number: "" });
            toast.success("Dispatch added"); refresh();
        } catch { toast.error("Failed to add dispatch"); }
    };
    const syncKit = async () => {
        try {
            const r = await axiosApi.post("/newsletter/sync-broadcasts");
            toast.success(`Synced ${r.data.added} new newsletters from Kit`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sync failed — check API key in .env");
        }
    };
    return (
        <div className="grid md:grid-cols-12 gap-5">
            <div className="md:col-span-5 panel p-6">
                <div className="overline-cobalt mb-4">[New · Dispatch]</div>
                <form onSubmit={submit} data-testid="new-post-form">
                    <input className="input-tech mb-3" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                    <textarea className="input-tech mb-3" rows={3} placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} required />
                    <input className="input-tech mb-3" placeholder="Link URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required />
                    <input className="input-tech mb-4" placeholder="Issue # (optional)" value={form.issue_number} onChange={(e) => setForm({ ...form, issue_number: e.target.value })} />
                    <button type="submit" className="btn-primary w-full justify-center"><Plus size={14} /> Add dispatch</button>
                </form>
                <button onClick={syncKit} className="btn-ghost w-full justify-center mt-3" data-testid="kit-sync-btn">
                    <RefreshCw size={13} /> Sync from Kit
                </button>
                <p className="overline mt-3">Pulls subjects + dates from Kit. Requires CONVERTKIT_API_SECRET in .env.</p>
                <div className="mt-8">
                    <div className="overline mb-3">Subscribers · {subs.length}</div>
                    <div className="max-h-80 overflow-y-auto panel-sub" data-testid="subscribers-list">
                        {subs.length === 0 && <div className="p-4 overline">No subscribers yet.</div>}
                        {subs.map((s) => (
                            <div
                                key={s.id}
                                className="p-3 border-b font-mono-tech text-[11px] flex justify-between"
                                style={{ borderColor: "var(--rule-soft)" }}
                            >
                                <span style={{ color: "var(--ink)" }}>{s.email}</span>
                                <span style={{ color: "var(--muted)" }}>{new Date(s.created_at).toLocaleDateString()}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="md:col-span-7">
                <div className="overline mb-3">Published · {posts.length}</div>
                <ul className="panel" data-testid="admin-posts">
                    {posts.map((p) => (
                        <li
                            key={p.id}
                            className="px-5 py-5 grid grid-cols-12 gap-3 items-baseline border-b last:border-b-0"
                            style={{ borderColor: "var(--rule-soft)" }}
                        >
                            <div className="col-span-2 font-mono-tech text-[11px]" style={{ color: "var(--cobalt)" }}>
                                {p.issue_number ? `№ ${p.issue_number}` : "—"}
                            </div>
                            <div className="col-span-9">
                                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: "var(--ink)" }}>{p.title}</h3>
                                <p className="text-[13px]" style={{ color: "var(--ink-2)" }}>{p.excerpt}</p>
                            </div>
                            <button
                                onClick={async () => { await Newsletter.deletePost(p.id); refresh(); }}
                                className="col-span-1 hover:text-[var(--orange)] transition-colors"
                                style={{ color: "var(--muted)" }}
                                data-testid={`delete-post-${p.id}`}
                            >
                                <Trash2 size={14} />
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function SubscribersTab() {
    const [subs, setSubs] = useState([]);
    const [filter, setFilter] = useState("all"); // all | community | newsletter_only
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        setLoading(true);
        try {
            const data = await Newsletter.subscribers();
            setSubs(data);
        } catch {
            toast.error("Failed to load subscribers");
        }
        setLoading(false);
    };
    useEffect(() => {
        refresh();
    }, []);

    const filtered = subs.filter((s) => {
        if (filter === "community" && !s.clicked_community) return false;
        if (filter === "newsletter_only" && s.clicked_community) return false;
        if (search) {
            const q = search.toLowerCase();
            const blob = [
                s.email,
                s.first_name,
                s.utm_source,
                s.utm_medium,
                s.utm_campaign,
                s.referrer,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            if (!blob.includes(q)) return false;
        }
        return true;
    });

    const total = subs.length;
    const community = subs.filter((s) => s.clicked_community).length;
    const newsletterOnly = total - community;

    // Source breakdown
    const sourceCounts = {};
    for (const s of subs) {
        const src = s.utm_source || "(unattributed)";
        sourceCounts[src] = (sourceCounts[src] || 0) + 1;
    }
    const sourceRanked = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]);

    const exportCsv = () => {
        const rows = [
            [
                "email",
                "first_name",
                "subscribed_at",
                "clicked_community",
                "utm_source",
                "utm_medium",
                "utm_campaign",
                "utm_content",
                "referrer",
                "landing_page",
                "synced_to_kit",
            ],
            ...filtered.map((s) => [
                s.email,
                s.first_name || "",
                s.created_at || "",
                s.clicked_community ? "yes" : "no",
                s.utm_source || "",
                s.utm_medium || "",
                s.utm_campaign || "",
                s.utm_content || "",
                s.referrer || "",
                s.landing_page || "",
                s.synced_to_convertkit ? "yes" : "no",
            ]),
        ];
        const csv = rows
            .map((r) =>
                r
                    .map((v) => {
                        const s = String(v ?? "");
                        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                    })
                    .join(","),
            )
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `frontier-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
    };

    return (
        <div data-testid="subscribers-tab" className="grid gap-5">
            {/* Stat row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard k="Total" v={total} />
                <StatCard k="Clicked Community" v={community} italic />
                <StatCard k="Newsletter Only" v={newsletterOnly} />
                <StatCard
                    k="Conversion"
                    v={total > 0 ? `${Math.round((community / total) * 100)}%` : "—"}
                />
            </div>

            {/* Source breakdown */}
            <div className="panel p-6">
                <div className="overline-cobalt mb-4">
                    [Attribution · Top Sources]
                </div>
                {sourceRanked.length === 0 ? (
                    <div className="overline">No data yet.</div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {sourceRanked.slice(0, 12).map(([src, count]) => (
                            <div
                                key={src}
                                className="flex items-center justify-between px-3 py-2"
                                style={{ borderBottom: "1px solid var(--rule-soft)" }}
                            >
                                <span
                                    className="font-mono-tech text-[11px]"
                                    style={{ color: "var(--ink-2)" }}
                                >
                                    {src}
                                </span>
                                <span
                                    className="font-mono-tech text-[12px]"
                                    style={{ color: "var(--cobalt)", fontWeight: 600 }}
                                >
                                    {count}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Toolbar */}
            <div className="panel p-4 flex flex-wrap items-center gap-3">
                <input
                    className="input-tech flex-1 min-w-[200px]"
                    placeholder="Search by email, name, source, campaign…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="subscribers-search"
                />
                <select
                    className="input-tech"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    data-testid="subscribers-filter"
                >
                    <option value="all">All ({total})</option>
                    <option value="community">
                        Clicked Community ({community})
                    </option>
                    <option value="newsletter_only">
                        Newsletter Only ({newsletterOnly})
                    </option>
                </select>
                <button
                    onClick={refresh}
                    className="btn-ghost btn-small"
                    data-testid="subscribers-refresh"
                >
                    <RefreshCw size={12} /> Refresh
                </button>
                <button
                    onClick={exportCsv}
                    className="btn-primary btn-small"
                    data-testid="subscribers-export"
                >
                    Export CSV
                </button>
            </div>

            {/* Table */}
            <div className="panel p-0 overflow-x-auto">
                <table className="w-full text-[12.5px]" data-testid="subscribers-table">
                    <thead>
                        <tr
                            style={{
                                background: "var(--panel-2)",
                                borderBottom: "1px solid var(--rule-soft)",
                            }}
                        >
                            <th className="text-left px-4 py-3 font-mono-tech text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--ink-2)" }}>
                                Email
                            </th>
                            <th className="text-left px-4 py-3 font-mono-tech text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--ink-2)" }}>
                                Joined
                            </th>
                            <th className="text-left px-4 py-3 font-mono-tech text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--ink-2)" }}>
                                Status
                            </th>
                            <th className="text-left px-4 py-3 font-mono-tech text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--ink-2)" }}>
                                Source
                            </th>
                            <th className="text-left px-4 py-3 font-mono-tech text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--ink-2)" }}>
                                Campaign
                            </th>
                            <th className="text-left px-4 py-3 font-mono-tech text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--ink-2)" }}>
                                Kit
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="px-4 py-10 text-center overline">
                                    Loading subscribers…
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-4 py-10 text-center overline">
                                    No subscribers match your filters.
                                </td>
                            </tr>
                        ) : (
                            filtered.map((s) => (
                                <SubscriberRow key={s.id} s={s} />
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SubscriberRow({ s }) {
    const joined = s.created_at ? new Date(s.created_at) : null;
    const joinedStr = joined
        ? joined.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
          })
        : "—";

    return (
        <tr
            style={{ borderBottom: "1px solid var(--rule-soft)" }}
            data-testid="subscriber-row"
        >
            <td className="px-4 py-3" style={{ color: "var(--ink)" }}>
                <div style={{ fontWeight: 600 }}>{s.email}</div>
                {s.first_name ? (
                    <div
                        className="text-[11px]"
                        style={{ color: "var(--muted)" }}
                    >
                        {s.first_name}
                    </div>
                ) : null}
            </td>
            <td className="px-4 py-3 font-mono-tech text-[11px]" style={{ color: "var(--ink-2)" }}>
                {joinedStr}
            </td>
            <td className="px-4 py-3">
                {s.clicked_community ? (
                    <span
                        className="font-mono-tech text-[10px] tracking-[0.2em] uppercase px-2 py-1"
                        style={{
                            color: "var(--cobalt)",
                            background: "rgba(26,63,204,0.08)",
                            border: "1px solid var(--cobalt)",
                        }}
                    >
                        Community
                    </span>
                ) : (
                    <span
                        className="font-mono-tech text-[10px] tracking-[0.2em] uppercase px-2 py-1"
                        style={{
                            color: "var(--muted)",
                            background: "transparent",
                            border: "1px solid var(--rule-soft)",
                        }}
                    >
                        Newsletter
                    </span>
                )}
            </td>
            <td className="px-4 py-3 font-mono-tech text-[11px]" style={{ color: "var(--ink-2)" }}>
                {s.utm_source || "—"}
                {s.utm_medium ? ` · ${s.utm_medium}` : ""}
            </td>
            <td className="px-4 py-3 font-mono-tech text-[11px]" style={{ color: "var(--ink-2)" }}>
                {s.utm_campaign || "—"}
            </td>
            <td className="px-4 py-3 font-mono-tech text-[11px]">
                {s.synced_to_convertkit ? (
                    <span style={{ color: "var(--cobalt)" }}>✓ Synced</span>
                ) : (
                    <span style={{ color: "var(--muted)" }}>—</span>
                )}
            </td>
        </tr>
    );
}


function YouTubeTab() {
    const [videos, setVideos] = useState([]);
    const [form, setForm] = useState({ video_id: "", title: "", description: "" });
    const refresh = async () => setVideos(await YouTubeApi.list().catch(() => []));
    useEffect(() => { refresh(); }, []);
    const submit = async (e) => {
        e.preventDefault();
        try { await YouTubeApi.add(form); setForm({ video_id: "", title: "", description: "" }); toast.success("Video added"); refresh(); }
        catch { toast.error("Failed to add video"); }
    };
    const sync = async () => {
        try { const res = await YouTubeApi.sync(); toast.success(`Synced ${res.added} videos`); refresh(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Sync requires YOUTUBE_API_KEY + channel_id"); }
    };
    return (
        <div className="grid md:grid-cols-12 gap-5">
            <div className="md:col-span-5 panel p-6">
                <div className="overline-cobalt mb-4">[Add · Video]</div>
                <form onSubmit={submit} data-testid="new-video-form">
                    <input className="input-tech mb-3" placeholder="YouTube Video ID (e.g. dQw4w9WgXcQ)" value={form.video_id} onChange={(e) => setForm({ ...form, video_id: e.target.value })} required />
                    <input className="input-tech mb-3" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                    <textarea className="input-tech mb-4" rows={3} placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    <button type="submit" className="btn-primary w-full justify-center"><Plus size={14} /> Add video</button>
                </form>
                <button onClick={sync} className="btn-ghost w-full justify-center mt-3" data-testid="yt-sync-btn">
                    <RefreshCw size={13} /> Sync from YouTube API
                </button>
                <p className="overline mt-3">Sync requires YOUTUBE_API_KEY + channel ID in settings.</p>
            </div>
            <div className="md:col-span-7">
                <div className="overline mb-3">Library · {videos.length}</div>
                <ul className="panel" data-testid="admin-videos">
                    {videos.length === 0 && <li className="p-5 overline">No videos added yet.</li>}
                    {videos.map((v) => (
                        <li key={v.id} className="px-5 py-4 grid grid-cols-12 gap-3 items-center border-b last:border-b-0" style={{ borderColor: "var(--rule-soft)" }}>
                            <img src={v.thumbnail} alt="" className="col-span-3 aspect-video object-cover" />
                            <div className="col-span-8">
                                <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>{v.title}</div>
                                <div className="font-mono-tech text-[10px]" style={{ color: "var(--muted)" }}>{v.video_id}</div>
                            </div>
                            <button onClick={async () => { await YouTubeApi.remove(v.id); refresh(); }} className="col-span-1 hover:text-[var(--orange)]" style={{ color: "var(--muted)" }}>
                                <Trash2 size={14} />
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function EventsTab() {
    const [events, setEvents] = useState([]);
    const [form, setForm] = useState({ title: "", description: "", date: "", location: "", event_type: "event", url: "" });
    const refresh = async () => setEvents(await Events.list().catch(() => []));
    useEffect(() => { refresh(); }, []);
    const submit = async (e) => {
        e.preventDefault();
        try {
            await Events.create({ ...form, date: new Date(form.date).toISOString() });
            setForm({ title: "", description: "", date: "", location: "", event_type: "event", url: "" });
            toast.success("Event added"); refresh();
        } catch { toast.error("Failed to add event"); }
    };
    return (
        <div className="grid md:grid-cols-12 gap-5">
            <div className="md:col-span-5 panel p-6">
                <div className="overline-cobalt mb-4">[New · Event]</div>
                <form onSubmit={submit} data-testid="new-event-form">
                    <input className="input-tech mb-3" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                    <input type="datetime-local" className="input-tech mb-3" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                    <input className="input-tech mb-3" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                    <select className="input-tech mb-3" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}>
                        <option value="event">Event</option>
                        <option value="livestream">Livestream</option>
                        <option value="in-person">In-person</option>
                        <option value="conference">Conference</option>
                    </select>
                    <input className="input-tech mb-3" placeholder="URL (optional)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
                    <textarea className="input-tech mb-4" rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    <button type="submit" className="btn-primary w-full justify-center"><Plus size={14} /> Add event</button>
                </form>
            </div>
            <div className="md:col-span-7">
                <div className="overline mb-3">Upcoming · {events.length}</div>
                <ul className="panel" data-testid="admin-events">
                    {events.map((ev) => (
                        <li key={ev.id} className="px-5 py-5 grid grid-cols-12 gap-3 items-baseline border-b last:border-b-0" style={{ borderColor: "var(--rule-soft)" }}>
                            <div className="col-span-3 font-mono-tech text-[11px]" style={{ color: "var(--cobalt)" }}>{new Date(ev.date).toLocaleString()}</div>
                            <div className="col-span-8">
                                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: "var(--ink)" }}>{ev.title}</h3>
                                <p className="text-[12px]" style={{ color: "var(--ink-2)" }}>{ev.location} · {ev.event_type}</p>
                            </div>
                            <button onClick={async () => { await Events.remove(ev.id); refresh(); }} className="col-span-1 hover:text-[var(--orange)]" style={{ color: "var(--muted)" }}>
                                <Trash2 size={14} />
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function BookingTab() {
    const [slots, setSlots] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [slotForm, setSlotForm] = useState({ starts_at: "", duration_minutes: 30 });
    const refresh = async () => {
        setSlots(await Booking.allSlots().catch(() => []));
        setBookings(await Booking.bookings().catch(() => []));
    };
    useEffect(() => { refresh(); }, []);
    const submit = async (e) => {
        e.preventDefault();
        try {
            await Booking.createSlot({ starts_at: new Date(slotForm.starts_at).toISOString(), duration_minutes: Number(slotForm.duration_minutes) });
            setSlotForm({ starts_at: "", duration_minutes: 30 });
            toast.success("Slot added"); refresh();
        } catch { toast.error("Failed to add slot"); }
    };
    return (
        <div className="grid md:grid-cols-12 gap-5">
            <div className="md:col-span-5 panel p-6">
                <div className="overline-cobalt mb-4">[New · Slot]</div>
                <form onSubmit={submit} data-testid="new-slot-form">
                    <input type="datetime-local" className="input-tech mb-3" value={slotForm.starts_at} onChange={(e) => setSlotForm({ ...slotForm, starts_at: e.target.value })} required />
                    <input type="number" className="input-tech mb-4" placeholder="Duration (minutes)" value={slotForm.duration_minutes} onChange={(e) => setSlotForm({ ...slotForm, duration_minutes: e.target.value })} required />
                    <button type="submit" className="btn-primary w-full justify-center"><Plus size={14} /> Add slot</button>
                </form>
                <div className="mt-8">
                    <div className="overline mb-3">Slots · {slots.length}</div>
                    <ul className="max-h-72 overflow-y-auto panel-sub" data-testid="admin-slots">
                        {slots.map((s) => (
                            <li key={s.id} className="p-3 border-b flex justify-between items-center font-mono-tech text-[11px]" style={{ borderColor: "var(--rule-soft)" }}>
                                <span style={{ color: "var(--ink)" }}>{new Date(s.starts_at).toLocaleString()} · {s.duration_minutes}m</span>
                                <span className="flex items-center gap-3">
                                    <span style={{ color: s.is_booked ? "var(--cobalt)" : "var(--muted)", fontWeight: 600 }}>
                                        {s.is_booked ? "BOOKED" : "OPEN"}
                                    </span>
                                    {!s.is_booked && (
                                        <button onClick={async () => { await Booking.deleteSlot(s.id); refresh(); }} className="hover:text-[var(--orange)]" style={{ color: "var(--muted)" }}>
                                            <Trash2 size={12} />
                                        </button>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="md:col-span-7">
                <div className="overline mb-3">Bookings · {bookings.length}</div>
                <ul className="panel" data-testid="admin-bookings">
                    {bookings.length === 0 && <li className="p-5 overline">No bookings yet.</li>}
                    {bookings.map((b) => (
                        <li key={b.id} className="px-5 py-5 grid grid-cols-12 gap-3 items-baseline border-b last:border-b-0" style={{ borderColor: "var(--rule-soft)" }}>
                            <div className="col-span-4">
                                <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: "var(--ink)" }}>{b.name}</div>
                                <div className="font-mono-tech text-[11px]" style={{ color: "var(--muted)" }}>{b.email}</div>
                            </div>
                            <div className="col-span-5 text-[13px]" style={{ color: "var(--ink-2)" }}>{b.notes}</div>
                            <div className="col-span-3 text-right">
                                <div className="font-mono-tech text-[10.5px] font-semibold" style={{ color: b.status === "confirmed" ? "var(--cobalt)" : "var(--muted)" }}>
                                    {b.status.toUpperCase()}
                                </div>
                                <div className="font-mono-tech text-[10px]" style={{ color: "var(--muted)" }}>${b.amount_usd}</div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function SettingsTab() {
    const [s, setS] = useState(null);
    useEffect(() => { Settings.get().then(setS).catch(() => {}); }, []);
    if (!s) return <div className="overline">Loading…</div>;
    const save = async (e) => {
        e.preventDefault();
        try {
            const updated = await Settings.update({ ...s, session_price_usd: Number(s.session_price_usd) });
            setS(updated); toast.success("Settings saved");
        } catch { toast.error("Failed to save"); }
    };
    return (
        <form onSubmit={save} className="max-w-2xl panel p-8 space-y-4" data-testid="settings-form">
            <div>
                <label className="overline block mb-2">Founder name</label>
                <input className="input-tech" value={s.founder_name || ""} onChange={(e) => setS({ ...s, founder_name: e.target.value })} />
            </div>
            <div>
                <label className="overline block mb-2">Founder title</label>
                <input className="input-tech" value={s.founder_title || ""} onChange={(e) => setS({ ...s, founder_title: e.target.value })} />
            </div>
            <div>
                <label className="overline block mb-2">YouTube channel ID (UC…)</label>
                <input className="input-tech" value={s.youtube_channel_id || ""} onChange={(e) => setS({ ...s, youtube_channel_id: e.target.value })} />
            </div>
            <div>
                <label className="overline block mb-2">YouTube channel handle</label>
                <input className="input-tech" value={s.youtube_channel_handle || ""} onChange={(e) => setS({ ...s, youtube_channel_handle: e.target.value })} />
            </div>
            <div>
                <label className="overline block mb-2">ConvertKit Form ID</label>
                <input className="input-tech" value={s.convertkit_form_id || ""} onChange={(e) => setS({ ...s, convertkit_form_id: e.target.value })} />
            </div>
            <div>
                <label className="overline block mb-2">Community Typeform URL</label>
                <input className="input-tech" placeholder="https://yourname.typeform.com/to/abc123" value={s.community_typeform_url || ""} onChange={(e) => setS({ ...s, community_typeform_url: e.target.value })} />
            </div>
            <div>
                <label className="overline block mb-2">Session price (USD)</label>
                <input type="number" className="input-tech" value={s.session_price_usd} onChange={(e) => setS({ ...s, session_price_usd: e.target.value })} />
            </div>
            <button className="btn-primary" data-testid="settings-save">Save</button>
        </form>
    );
}
